import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  BRANDING_COLORS,
  BRANDING_FONTS,
  DEFAULT_BRANDING,
  brandingSchema,
  type BrandingConfig,
  type BrandingState,
} from '../shared/branding';
import { ApiRequestError } from './api';
export async function brandingApi(
  path = '/api/admin/branding',
  method = 'GET',
  body?: unknown,
  csrfToken?: string | null,
): Promise<BrandingState> {
  const response = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const result = (await response.json()) as BrandingState & { error?: string };
  if (!response.ok)
    throw new ApiRequestError(result.error || 'Could not load branding', response.status);
  return result;
}
type BrandingContextValue = {
  config: BrandingConfig;
  preview: BrandingConfig | null;
  setPreview: (value: BrandingConfig | null) => void;
  refresh: () => Promise<void>;
};
const BrandingContext = createContext<BrandingContextValue>({
  config: DEFAULT_BRANDING,
  preview: null,
  setPreview: () => {},
  refresh: async () => {},
});
export const useBranding = () => useContext(BrandingContext);
export function BrandingProvider({
  children,
  superAdmin,
}: {
  children: ReactNode;
  superAdmin: boolean;
}) {
  const [published, setPublished] = useState(DEFAULT_BRANDING);
  const [draftPreview, setDraftPreview] = useState<BrandingConfig | null>(null);
  const preview = superAdmin ? draftPreview : null;
  const config = preview || published;
  const refresh = useCallback(async () => {
    const response = await fetch('/api/branding', { credentials: 'same-origin' });
    if (!response.ok) throw new Error('Could not load published branding');
    const value = (await response.json()) as { config: unknown };
    setPublished(brandingSchema.parse(value.config));
  }, []);
  useEffect(() => {
    void refresh().catch(() => {});
  }, [refresh]);
  useEffect(() => {
    if (!superAdmin) {
      setDraftPreview(null);
      return;
    }
    try {
      const saved = sessionStorage.getItem('branding-preview');
      if (saved) setDraftPreview(brandingSchema.parse(JSON.parse(saved)));
    } catch {
      sessionStorage.removeItem('branding-preview');
    }
  }, [superAdmin]);
  const setPreview = (value: BrandingConfig | null) => {
    if (value && superAdmin) sessionStorage.setItem('branding-preview', JSON.stringify(value));
    else sessionStorage.removeItem('branding-preview');
    setDraftPreview(value);
  };
  useEffect(() => {
    const root = document.documentElement;
    const colors = BRANDING_COLORS[config.colorPreset];
    const fonts = BRANDING_FONTS[config.fontPreset];
    const values: Record<string, string> = {
      '--branding-cream': colors.cream,
      '--branding-paper': colors.paper,
      '--branding-paper-deep': colors.paperDeep,
      '--branding-forest': colors.primary,
      '--branding-forest-dark': colors.dark,
      '--branding-forest-soft': colors.soft,
      '--branding-primary-light': colors.primaryLight,
      '--branding-primary-pale': colors.primaryPale,
      '--branding-accent': colors.accent,
      '--branding-accent-dark': colors.accentDark,
      '--branding-accent-light': colors.accentLight,
      '--brand-body-font': fonts.body,
      '--brand-heading-font': fonts.heading,
    };
    for (const [name, value] of Object.entries(values)) root.style.setProperty(name, value);
    return () => {
      for (const name of Object.keys(values)) root.style.removeProperty(name);
    };
  }, [config]);
  return (
    <BrandingContext.Provider value={{ config, preview, setPreview, refresh }}>
      {preview && (
        <div className="branding-preview-banner" role="status">
          <b>Draft branding preview</b>
          <span>Only you see these changes.</span>
          <a href="/admin/branding">Return to branding</a>
          <button type="button" onClick={() => setPreview(null)}>
            Exit preview
          </button>
        </div>
      )}
      {children}
    </BrandingContext.Provider>
  );
}
